import { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Calendar as CalendarIcon, Bell, BellOff, ChevronLeft, ChevronRight, Clock, MapPin, CheckCircle, Star, ArrowLeft } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { getTeamFlag } from '../utils/teamFlags';

const API = process.env.REACT_APP_BACKEND_URL;

function getDayLabel(dateStr) {
  const d = new Date(dateStr + 'T00:00:00');
  const now = new Date();
  const today = now.toISOString().slice(0, 10);
  const tomorrow = new Date(now.getTime() + 86400000).toISOString().slice(0, 10);
  if (dateStr === today) return 'Today';
  if (dateStr === tomorrow) return 'Tomorrow';
  return d.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });
}

function isWithinHours(dateStr, timeStr, hours) {
  if (!dateStr || !timeStr) return false;
  const dt = new Date(`${dateStr}T${timeStr}:00Z`);
  const now = new Date();
  const diff = dt - now;
  return diff > 0 && diff < hours * 3600000;
}

function formatTime(timeStr) {
  if (!timeStr) return '';
  try {
    const [h, m] = timeStr.split(':');
    const d = new Date();
    d.setUTCHours(parseInt(h), parseInt(m));
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch {
    return timeStr;
  }
}

export default function MatchCalendar() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(7);
  const [reminders, setReminders] = useState(() => {
    return JSON.parse(localStorage.getItem('afrosports_reminders') || '[]');
  });

  useEffect(() => {
    const token = localStorage.getItem('token');
    fetch(`${API}/api/calendar?days=${days}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then(r => r.json())
      .then(json => { setData(json); setLoading(false); })
      .catch(() => setLoading(false));
  }, [days]);

  const dates = useMemo(() => {
    if (!data?.grouped) return [];
    return Object.keys(data.grouped).sort();
  }, [data]);

  const today = new Date().toISOString().slice(0, 10);
  const todayIndex = dates.indexOf(today);

  const toggleReminder = (matchId) => {
    setReminders(prev => {
      const next = prev.includes(matchId)
        ? prev.filter(r => r !== matchId)
        : [...prev, matchId];
      localStorage.setItem('afrosports_reminders', JSON.stringify(next));
      return next;
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a]">
        <Navbar />
        <div className="flex items-center justify-center h-[60vh]">
          <div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a]" data-testid="calendar-page">
      <Navbar />

      {/* Header */}
      <div className="bg-gradient-to-b from-[#111] to-[#0a0a0a] border-b border-white/5">
        <div className="max-w-3xl mx-auto px-4 py-6">
          <div className="flex items-center gap-3 mb-4">
            <Link to="/" className="w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors" data-testid="calendar-back-btn">
              <ArrowLeft className="w-4 h-4 text-white/60" />
            </Link>
            <CalendarIcon className="w-5 h-5 text-emerald-500" />
            <h1 className="text-white font-bold text-lg">Match Calendar</h1>
          </div>

          {/* Personalization info */}
          {data?.is_personalized ? (
            <p className="text-emerald-400/70 text-xs font-medium" data-testid="calendar-personalized">
              <Star className="w-3 h-3 inline mr-1" />
              Showing matches for your followed teams: {data.followed_teams.join(', ')}
            </p>
          ) : (
            <p className="text-white/40 text-xs" data-testid="calendar-default">
              Showing major African football matches. Follow teams for a personalized calendar.
            </p>
          )}

          {/* Day range controls */}
          <div className="flex items-center gap-2 mt-4">
            {[7, 14].map(d => (
              <button
                key={d}
                onClick={() => setDays(d)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                  days === d
                    ? 'bg-emerald-500/15 border border-emerald-500/30 text-emerald-400'
                    : 'bg-white/[0.04] border border-white/[0.08] text-white/50 hover:text-white'
                }`}
                data-testid={`days-${d}`}
              >
                {d} Days
              </button>
            ))}
            {todayIndex >= 0 && (
              <button
                onClick={() => document.getElementById(`date-${today}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                className="ml-auto px-3 py-1.5 rounded-full text-xs font-semibold bg-white/[0.04] border border-white/[0.08] text-white/50 hover:text-white transition-colors"
                data-testid="today-shortcut"
              >
                <Clock className="w-3 h-3 inline mr-1" />
                Today
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Calendar body */}
      <div className="max-w-3xl mx-auto px-4 py-6">
        {data?.total === 0 ? (
          <div className="text-center py-16" data-testid="calendar-empty">
            <CalendarIcon className="w-12 h-12 text-white/10 mx-auto mb-3" />
            <p className="text-white/50 text-sm">No upcoming matches found</p>
            <p className="text-white/30 text-xs mt-1">Follow teams on the Match Day page to see their upcoming games here.</p>
          </div>
        ) : (
          <div className="space-y-8">
            {dates.map(dateStr => {
              const events = data.grouped[dateStr] || [];
              const isToday = dateStr === today;
              return (
                <div key={dateStr} id={`date-${dateStr}`} data-testid={`date-group-${dateStr}`}>
                  {/* Date header */}
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`w-10 h-10 rounded-xl flex flex-col items-center justify-center shrink-0 ${
                      isToday ? 'bg-emerald-500/15 border border-emerald-500/30' : 'bg-white/[0.04] border border-white/[0.06]'
                    }`}>
                      <span className={`text-[10px] font-bold uppercase leading-none ${isToday ? 'text-emerald-400' : 'text-white/40'}`}>
                        {new Date(dateStr + 'T00:00:00').toLocaleDateString([], { weekday: 'short' })}
                      </span>
                      <span className={`text-sm font-black leading-tight ${isToday ? 'text-emerald-400' : 'text-white'}`}>
                        {new Date(dateStr + 'T00:00:00').getDate()}
                      </span>
                    </div>
                    <div>
                      <h3 className={`font-bold text-sm ${isToday ? 'text-emerald-400' : 'text-white'}`}>
                        {getDayLabel(dateStr)}
                      </h3>
                      <p className="text-white/30 text-[11px]">{events.length} match{events.length !== 1 ? 'es' : ''}</p>
                    </div>
                  </div>

                  {/* Match cards */}
                  <div className="space-y-2 sm:ml-[52px]">
                    {events.map((evt, i) => {
                      const homeFlag = getTeamFlag(evt.home);
                      const awayFlag = getTeamFlag(evt.away);
                      const isSoon = isWithinHours(evt.date, evt.time, 3);
                      const matchId = evt.id;
                      const isReminded = matchId && reminders.includes(matchId);

                      return (
                        <div
                          key={`${evt.home}-${evt.away}-${i}`}
                          className={`p-3 rounded-xl border transition-all cursor-pointer group ${
                            isSoon
                              ? 'bg-emerald-500/[0.06] border-emerald-500/15 hover:border-emerald-500/30'
                              : 'bg-white/[0.02] border-white/[0.06] hover:border-white/10 hover:bg-white/[0.04]'
                          }`}
                          onClick={() => {
                            if (evt.id) navigate(`/matchday/${evt.id}`);
                          }}
                          data-testid={`calendar-match-${i}`}
                        >
                          <div className="flex items-start gap-3">
                            {/* Time column */}
                            <div className="w-12 shrink-0 text-center pt-0.5">
                              {evt.time ? (
                                <span className={`text-sm font-bold tabular-nums ${isSoon ? 'text-emerald-400' : 'text-white/70'}`}>
                                  {formatTime(evt.time)}
                                </span>
                              ) : (
                                <span className="text-white/30 text-xs">TBD</span>
                              )}
                              {isSoon && (
                                <span className="block text-emerald-400 text-[9px] font-bold mt-0.5 animate-pulse">SOON</span>
                              )}
                            </div>

                            {/* Match info */}
                            <div className="flex-1 min-w-0">
                              <div className="space-y-1.5">
                                <div className="flex items-center gap-2">
                                  {homeFlag ? <span className="text-base">{homeFlag}</span> : (
                                    <span className="w-5 h-5 rounded-full bg-white/10 flex items-center justify-center text-[7px] font-bold text-white/50 shrink-0">{evt.home.slice(0,3).toUpperCase()}</span>
                                  )}
                                  <span className="text-white font-semibold text-sm truncate">{evt.home}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  {awayFlag ? <span className="text-base">{awayFlag}</span> : (
                                    <span className="w-5 h-5 rounded-full bg-white/10 flex items-center justify-center text-[7px] font-bold text-white/50 shrink-0">{evt.away.slice(0,3).toUpperCase()}</span>
                                  )}
                                  <span className="text-white font-semibold text-sm truncate">{evt.away}</span>
                                </div>
                              </div>

                              {/* Meta row */}
                              <div className="flex items-center gap-2 mt-2 flex-wrap">
                                {evt.competition && (
                                  <span className="text-emerald-400/70 text-[10px] font-medium">{evt.competition}</span>
                                )}
                                {evt.venue && (
                                  <span className="text-white/25 text-[10px] flex items-center gap-0.5">
                                    <MapPin className="w-2.5 h-2.5" /> {evt.venue}
                                  </span>
                                )}
                                {evt.has_real_data && (
                                  <span className="flex items-center gap-0.5 text-emerald-400/60 text-[9px] font-medium" data-testid={`cal-verified-${i}`}>
                                    <CheckCircle className="w-2.5 h-2.5" /> Verified
                                  </span>
                                )}
                                {evt.followed_team && (
                                  <span className="text-amber-400/80 text-[9px] font-bold bg-amber-500/10 px-1.5 py-0.5 rounded-full" data-testid={`cal-following-${i}`}>
                                    <Star className="w-2.5 h-2.5 inline mr-0.5" />{evt.followed_team}
                                  </span>
                                )}
                              </div>
                            </div>

                            {/* Remind Me button */}
                            {matchId && (
                              <button
                                onClick={(e) => { e.stopPropagation(); toggleReminder(matchId); }}
                                className={`shrink-0 p-2 rounded-lg transition-all ${
                                  isReminded
                                    ? 'bg-emerald-500/15 text-emerald-400'
                                    : 'bg-white/[0.04] text-white/30 hover:text-white hover:bg-white/[0.08]'
                                }`}
                                data-testid={`cal-remind-${i}`}
                              >
                                {isReminded ? <BellOff className="w-4 h-4" /> : <Bell className="w-4 h-4" />}
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
